import * as React from "react";
import { Link, makeStyles } from "@fluentui/react-components";
import { CheckmarkStarburst16Filled, Open16Regular } from "@fluentui/react-icons";
import type { CardData } from "@/types";
import { getApprovalBadgeInfo, getHeaderText, getMatchBadgeInfo, getPrimaryCitationUrl } from "@/utils/card-utils";

const useStyles = makeStyles({
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "8px",
    marginBottom: "8px",
  },
  headerText: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
  },
  headerTitle: {
    fontSize: "12px",
    fontWeight: "bold",
  },
  linkIcon: {
    color: "#0078d4",
  },
  badges: {
    display: "flex",
    gap: "4px",
    alignItems: "center",
  },
  badgeExact: {
    padding: "4px 8px",
    backgroundColor: "#228b22",
    color: "white",
    borderRadius: "16px",
    fontSize: "10px",
    fontWeight: "bold",
    textTransform: "uppercase",
  },
  badgePartial: {
    padding: "4px 8px",
    backgroundColor: "#fff3cd",
    color: "#856404",
    borderRadius: "16px",
    fontSize: "10px",
    fontWeight: "bold",
    textTransform: "uppercase",
  },
  badgePending: {
    padding: "4px 8px",
    backgroundColor: "#fff3cd",
    color: "#856404",
    borderRadius: "16px",
    fontSize: "10px",
    fontWeight: "bold",
    textTransform: "uppercase",
  },
  badgeApproved: {
    display: "flex",
    gap: "4px",
    alignItems: "center",
    padding: "4px 8px",
    backgroundColor: "#228b22",
    color: "white",
    borderRadius: "16px",
    fontSize: "10px",
    fontWeight: "bold",
    textTransform: "uppercase",
  },
  badgeRejected: {
    padding: "4px 8px",
    backgroundColor: "#ffeaa7",
    color: "#6c5401",
    borderRadius: "16px",
    fontSize: "10px",
    fontWeight: "bold",
    textTransform: "uppercase",
  },
  badgeNoMatch: {
    padding: "4px 8px",
    backgroundColor: "#666",
    color: "white",
    borderRadius: "16px",
    fontSize: "10px",
  },
});

type CardHeaderProps = {
  data: CardData;
  productId?: string;
  requestId?: string;
};

export const CardHeader: React.FC<CardHeaderProps> = ({ data, productId, requestId }) => {
  const styles = useStyles();

  const headerText = getHeaderText(data);
  const citationUrl = getPrimaryCitationUrl(data, productId, requestId);
  const matchBadge = getMatchBadgeInfo(data);
  const approvalBadge = getApprovalBadgeInfo(data.metadata?.approvalStatus);

  return (
    <div className={styles.header}>
      <div className={styles.headerText}>
        <div className={styles.headerTitle}>{headerText}</div>
        {citationUrl && (
          <Link href={citationUrl} appearance="subtle" target="_blank" rel="noopener noreferrer">
            <Open16Regular className={styles.linkIcon} />
          </Link>
        )}
      </div>
      <div className={styles.badges}>
        {matchBadge && (
          <div
            className={
              matchBadge.type === "exact"
                ? styles.badgeExact
                : matchBadge.type === "partial"
                  ? styles.badgePartial
                  : styles.badgeNoMatch
            }
          >
            {matchBadge.text}
          </div>
        )}
        {approvalBadge && (
          <div
            className={
              approvalBadge.type === "pending"
                ? styles.badgePending
                : approvalBadge.type === "approved"
                  ? styles.badgeApproved
                  : styles.badgeRejected
            }
          >
            {approvalBadge.type === "approved" && <CheckmarkStarburst16Filled />}
            {approvalBadge.text}
          </div>
        )}
      </div>
    </div>
  );
};
